import { inject, type InjectionKey } from "vue";
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Database,
  FolderTree,
  HelpCircle,
  Info,
  Inbox,
  Lightbulb,
  List,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  Plus,
  Save,
  Search,
  Star,
  Trash2,
  RotateCcw,
  X,
} from "lucide-vue-next";

export type IconComponent = unknown;
export type IconRegistry = Record<string, IconComponent>;

export interface IconRegistryInstance {
  custom: Map<string, IconComponent>;
  defaults: Map<string, IconComponent>;
}

export const iconRegistryKey: InjectionKey<IconRegistryInstance> = Symbol.for("lunagoai.vue-ui.iconRegistry") as InjectionKey<IconRegistryInstance>;

const builtInIcons: IconRegistry = {
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Database,
  FolderTree,
  HelpCircle,
  Info,
  Inbox,
  Lightbulb,
  List,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  Plus,
  Save,
  Search,
  Star,
  Trash2,
  RotateCcw,
  X,
};

export function normalizeIconName(name: string): string {
  return name
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
}

export function createIconRegistry(icons: IconRegistry = {}): IconRegistryInstance {
  const instance: IconRegistryInstance = {
    custom: new Map(),
    defaults: new Map(),
  };
  for (const [name, component] of Object.entries(builtInIcons)) {
    instance.defaults.set(name, component);
    instance.defaults.set(normalizeIconName(name), component);
  }
  registerIcons(icons, instance);
  return instance;
}

const defaultRegistry = createIconRegistry();

export function registerIcon(name: string, component: IconComponent, target: IconRegistryInstance = defaultRegistry): void {
  target.custom.set(name, component);
  target.custom.set(normalizeIconName(name), component);
}

export function registerIcons(icons: IconRegistry, target: IconRegistryInstance = defaultRegistry): void {
  for (const [name, component] of Object.entries(icons)) {
    registerIcon(name, component, target);
  }
}

export function resolveIcon(name: string, target: IconRegistryInstance = defaultRegistry): IconComponent | null {
  return target.custom.get(name)
    ?? target.custom.get(normalizeIconName(name))
    ?? target.defaults.get(name)
    ?? target.defaults.get(normalizeIconName(name))
    ?? null;
}

export function clearIconRegistry(): void {
  defaultRegistry.custom.clear();
}

export function useIconRegistry(): IconRegistryInstance {
  return inject(iconRegistryKey, defaultRegistry);
}
